import { Timestamp } from 'firebase/firestore';

/**
 * Recent Conversation Memory System Types
 * 
 * This system stores summaries of all chats about pets by name for the last 3 months,
 * providing context-aware AI responses and personalized pet care recommendations.
 */

export interface PetConversationSummary {
  id: string;
  userId: string;
  petName: string;
  petType: 'dog' | 'cat' | 'both';
  petBreed?: string;
  petAge?: number;
  conversationId: string;
  summary: string;
  keyTopics: string[];
  petCareAdvice: string[];
  trainingProgress?: string[];
  healthNotes?: string[];
  behaviorObservations?: string[];
  createdAt: Timestamp;
  lastUpdated: Timestamp;
  language: 'zh-CN' | 'es' | 'fr' | 'en';
  conversationCount: number; // Number of conversations about this pet
  totalMessages: number; // Total messages across all conversations
}

export interface RecentMemoryContext {
  petName: string;
  petType: 'dog' | 'cat' | 'both';
  recentSummaries: PetConversationSummary[];
  overallPetProfile: {
    personality: string[];
    preferences: string[];
    challenges: string[];
    achievements: string[];
    healthHistory: string[];
  };
  lastConversationDate: Timestamp;
  totalConversations: number;
}

export interface MemorySearchFilters {
  petName?: string;
  petType?: 'dog' | 'cat' | 'both';
  dateRange?: {
    start: Date;
    end: Date;
  };
  topics?: string[];
  language?: 'zh-CN' | 'es' | 'fr' | 'en';
}

export interface MemoryAnalytics {
  mostDiscussedTopics: Array<{
    topic: string;
    frequency: number;
    lastDiscussed: Timestamp;
  }>;
  petProgressTrends: Array<{
    date: Timestamp;
    progress: string;
    improvement: boolean;
  }>;
  conversationPatterns: {
    averageConversationLength: number;
    mostActiveTimeOfDay: string;
    preferredTopics: string[];
  };
  healthTracking: Array<{
    date: Timestamp;
    healthNote: string;
    severity: 'low' | 'medium' | 'high';
  }>;
}

export interface ConversationMemoryRequest {
  conversationId: string;
  userId: string;
  petName: string;
  petType: 'dog' | 'cat' | 'both';
  messages: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: Timestamp;
  }>;
  language: 'zh-CN' | 'es' | 'fr' | 'en';
  context?: {
    petBreed?: string;
    petAge?: number;
    healthIssues?: string[];
    trainingGoals?: string[];
  };
}

export interface MemoryGenerationResult {
  success: boolean;
  summary?: PetConversationSummary;
  error?: string;
  processingTime?: number;
}

export interface MemoryRetrievalResult {
  success: boolean;
  summaries?: PetConversationSummary[];
  context?: RecentMemoryContext;
  error?: string;
  retrievalTime?: number;
}

// Language-specific text content for memory system
export interface MemoryTexts {
  // Summary generation prompts
  summaryPrompt: string;
  topicExtractionPrompt: string;
  adviceExtractionPrompt: string;
  
  // UI labels
  recentConversations: string;
  petProfile: string;
  keyTopics: string;
  petCareAdvice: string;
  trainingProgress: string;
  healthNotes: string;
  behaviorObservations: string;
  lastConversation: string;
  totalConversations: string;
  personality: string;
  preferences: string;
  achievements: string;
  healthHistory: string;
  analytics: string;
  mostDiscussedTopics: string;
  conversationPatterns: string;
  loading: string;
  refresh: string;
  
  // Error messages
  memoryGenerationFailed: string;
  memoryRetrievalFailed: string;
  noRecentConversations: string;
  
  // Success messages
  memoryUpdated: string;
  summaryGenerated: string;
}

export const MEMORY_TEXTS: Record<string, MemoryTexts> = {
  'en': {
    summaryPrompt: "Generate a concise summary of this pet care conversation, focusing on key advice, training progress, and health observations.",
    topicExtractionPrompt: "Extract the main topics discussed in this pet care conversation.",
    adviceExtractionPrompt: "Extract specific pet care advice and recommendations given in this conversation.",
    recentConversations: "Recent Conversations",
    petProfile: "Pet Profile",
    keyTopics: "Key Topics",
    petCareAdvice: "Pet Care Advice",
    trainingProgress: "Training Progress",
    healthNotes: "Health Notes",
    behaviorObservations: "Behavior Observations",
    lastConversation: "Last Conversation",
    totalConversations: "Total Conversations",
    personality: "Personality",
    preferences: "Preferences",
    achievements: "Achievements",
    healthHistory: "Health History",
    analytics: "Analytics",
    mostDiscussedTopics: "Most Discussed Topics",
    conversationPatterns: "Conversation Patterns",
    loading: "Loading memory data...",
    refresh: "Refresh",
    memoryGenerationFailed: "Failed to generate conversation memory",
    memoryRetrievalFailed: "Failed to retrieve conversation memory",
    noRecentConversations: "No recent conversations found for this pet",
    memoryUpdated: "Conversation memory updated successfully",
    summaryGenerated: "Conversation summary generated successfully"
  },
  'zh-CN': {
    summaryPrompt: "生成这个宠物护理对话的简洁摘要，重点关注关键建议、训练进展和健康观察。",
    topicExtractionPrompt: "提取这个宠物护理对话中讨论的主要话题。",
    adviceExtractionPrompt: "提取这个对话中给出的具体宠物护理建议和推荐。",
    recentConversations: "最近对话",
    petProfile: "宠物档案",
    keyTopics: "关键话题",
    petCareAdvice: "宠物护理建议",
    trainingProgress: "训练进展",
    healthNotes: "健康记录",
    behaviorObservations: "行为观察",
    lastConversation: "最后对话",
    totalConversations: "总对话数",
    personality: "性格特征",
    preferences: "偏好习惯",
    achievements: "成就记录",
    healthHistory: "健康历史",
    analytics: "数据分析",
    mostDiscussedTopics: "热门话题",
    conversationPatterns: "对话模式",
    loading: "正在加载记忆数据...",
    refresh: "刷新",
    memoryGenerationFailed: "生成对话记忆失败",
    memoryRetrievalFailed: "检索对话记忆失败",
    noRecentConversations: "未找到此宠物的最近对话",
    memoryUpdated: "对话记忆更新成功",
    summaryGenerated: "对话摘要生成成功"
  },
  'es': {
    summaryPrompt: "Genera un resumen conciso de esta conversación sobre el cuidado de mascotas, enfocándote en consejos clave, progreso de entrenamiento y observaciones de salud.",
    topicExtractionPrompt: "Extrae los temas principales discutidos en esta conversación sobre el cuidado de mascotas.",
    adviceExtractionPrompt: "Extrae consejos específicos y recomendaciones de cuidado de mascotas dados en esta conversación.",
    recentConversations: "Conversaciones Recientes",
    petProfile: "Perfil de Mascota",
    keyTopics: "Temas Clave",
    petCareAdvice: "Consejos de Cuidado",
    trainingProgress: "Progreso de Entrenamiento",
    healthNotes: "Notas de Salud",
    behaviorObservations: "Observaciones de Comportamiento",
    lastConversation: "Última Conversación",
    totalConversations: "Total de Conversaciones",
    personality: "Personalidad",
    preferences: "Preferencias",
    achievements: "Logros",
    healthHistory: "Historial de Salud",
    analytics: "Análisis",
    mostDiscussedTopics: "Temas Más Discutidos",
    conversationPatterns: "Patrones de Conversación",
    loading: "Cargando datos de memoria...",
    refresh: "Actualizar",
    memoryGenerationFailed: "Error al generar memoria de conversación",
    memoryRetrievalFailed: "Error al recuperar memoria de conversación",
    noRecentConversations: "No se encontraron conversaciones recientes para esta mascota",
    memoryUpdated: "Memoria de conversación actualizada exitosamente",
    summaryGenerated: "Resumen de conversación generado exitosamente"
  },
  'fr': {
    summaryPrompt: "Générez un résumé concis de cette conversation sur les soins aux animaux, en vous concentrant sur les conseils clés, les progrès d'entraînement et les observations de santé.",
    topicExtractionPrompt: "Extrayez les sujets principaux discutés dans cette conversation sur les soins aux animaux.",
    adviceExtractionPrompt: "Extrayez les conseils spécifiques et recommandations de soins aux animaux donnés dans cette conversation.",
    recentConversations: "Conversations Récentes",
    petProfile: "Profil de l'Animal",
    keyTopics: "Sujets Clés",
    petCareAdvice: "Conseils de Soins",
    trainingProgress: "Progrès d'Entraînement",
    healthNotes: "Notes de Santé",
    behaviorObservations: "Observations Comportementales",
    lastConversation: "Dernière Conversation",
    totalConversations: "Total des Conversations",
    personality: "Personnalité",
    preferences: "Préférences",
    achievements: "Réalisations",
    healthHistory: "Historique de Santé",
    analytics: "Analyses",
    mostDiscussedTopics: "Sujets les Plus Discutés",
    conversationPatterns: "Modèles de Conversation",
    loading: "Chargement des données de mémoire...",
    refresh: "Actualiser",
    memoryGenerationFailed: "Échec de la génération de mémoire de conversation",
    memoryRetrievalFailed: "Échec de la récupération de mémoire de conversation",
    noRecentConversations: "Aucune conversation récente trouvée pour cet animal",
    memoryUpdated: "Mémoire de conversation mise à jour avec succès",
    summaryGenerated: "Résumé de conversation généré avec succès"
  }
};
